import { useCallback, useMemo, useState } from 'react';

export interface ITrigger {
  defaultValue?: boolean;
  activate: () => void;
  deactivate: () => void;
}

/**
 * @function
 * Create single trigger.
 * @param {ITrigger} props - {@link ITrigger} to be applied to trigger.
 */
export const useTrigger = (props?: Partial<ITrigger>): [boolean, ITrigger] => {
  const { activate: onActivate, deactivate: onDeactivate, defaultValue } = props || {};

  const [isActive, setActive] = useState(defaultValue || false);

  const activate = useCallback((): void => {
    if (onActivate) {
      onActivate();
    }
    setActive(true);
  }, [onActivate]);

  const deactivate = useCallback((): void => {
    if (onDeactivate) {
      onDeactivate();
    }
    setActive(false);
  }, [onDeactivate]);

  const context = useMemo(() => {
    return { activate, deactivate } as ITrigger;
  }, [activate, deactivate]);

  return [isActive, context];
};

/**
 * @function
 * Create multiple triggers.
 * @param {number} count - number of trigger to be returned. this param MUST be a fixed value.
 * @param {ITrigger} props - {@link ITrigger} to be applied to ALL triggers.
 */
export const useTriggers = (
  count: number,
  props?: Partial<ITrigger>
): [Pick<ITrigger, 'deactivate'>, ...[boolean, ITrigger][]] => {
  const triggers: [boolean, ITrigger][] = [];
  for (let x = 0; x < count; x++) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const trigger = useTrigger(props);
    triggers.push(trigger);
  }
  const deactivate = () => {
    triggers.forEach(([_, trigger]) => trigger.deactivate());
  };
  return [{ deactivate }, ...triggers];
};
